import { UserIdentity } from '../types/analytics'
import { Department } from '../data/departments'
import { config } from '../config/environment'

// API base URL from configuration
const API_BASE_URL = config.API_BASE_URL

const STORAGE_KEY = 'reach3_user_identity'

export interface UserRegistrationRequest {
  email: string
  department: Department
}

export interface UserRegistrationResponse {
  userId: string
  email: string
  department: Department
  isExistingUser: boolean
}

function generateUUID(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0
    const v = c === 'x' ? r : (r & 0x3 | 0x8)
    return v.toString(16)
  })
}

function getCurrentTimestamp(): string {
  return new Date().toISOString()
}

export class UserIdentityService {
  private static instance: UserIdentityService | null = null
  private identity: UserIdentity | null = null

  private constructor() {
    this.loadIdentity()
  }

  static getInstance(): UserIdentityService {
    if (!UserIdentityService.instance) {
      UserIdentityService.instance = new UserIdentityService()
    }
    return UserIdentityService.instance
  }

  private loadIdentity(): void {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        const parsed = JSON.parse(stored) as UserIdentity
        if (parsed.id && parsed.email && parsed.department) {
          this.identity = parsed
          return
        }
      }
    } catch (error) {
      console.warn('Failed to load user identity from localStorage:', error)
    }
    
    this.identity = null
  }

  private saveIdentity(): void {
    if (!this.identity) return

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.identity))
    } catch (error) {
      console.error('Failed to save user identity to localStorage:', error)
    }
  }

  async registerUser(request: UserRegistrationRequest): Promise<UserRegistrationResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/users/register`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      })

      if (!response.ok) {
        throw new Error(`Registration failed: ${response.statusText}`)
      }

      const result = await response.json()
      
      if (!result.success) {
        throw new Error(result.message || 'Registration failed')
      }

      const user = result.user
      this.identity = {
        id: user.id,
        email: user.email,
        department: user.department,
        created: user.created,
        updated: user.updated
      }

      this.saveIdentity()
      
      return {
        userId: user.id,
        email: user.email,
        department: user.department,
        isExistingUser: result.message === 'User already exists'
      }
    } catch (error) {
      console.error('User registration failed:', error)
      throw error
    }
  }

  getIdentity(): UserIdentity | null {
    return this.identity
  }

  hasIdentity(): boolean {
    return this.identity !== null
  }

  getUserId(): string | null {
    return this.identity?.id || null
  }

  getEmail(): string | null {
    return this.identity?.email || null
  }

  getDepartment(): Department | null {
    return this.identity?.department || null
  }

  clearIdentity(): void {
    this.identity = null
    try {
      localStorage.removeItem(STORAGE_KEY)
    } catch (error) {
      console.warn('Failed to clear user identity from localStorage:', error)
    }
  }

  static hasStoredIdentity(): boolean {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (!stored) return false
      
      const parsed = JSON.parse(stored)
      return !!(parsed.id && parsed.email && parsed.department)
    } catch {
      return false
    }
  }
}

export const userIdentityService = UserIdentityService.getInstance()